import { supabase } from "./supabase";

export interface OnboardingState {
  hasCompletedOnboarding: boolean;
  currentStep: number;
  lastShownAt: string | null;
  skippedAt: string | null;
}

const ONBOARDING_STORAGE_KEY = "pill-scheduler-onboarding";

// Local storage helpers
export function getLocalOnboardingState(): OnboardingState | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function setLocalOnboardingState(state: OnboardingState): void {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Handle localStorage errors gracefully
  }
}

export function clearLocalOnboardingState(): void {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch {
    // Handle localStorage errors gracefully
  }
}

// Database helpers for authenticated users
export async function getUserOnboardingState(): Promise<OnboardingState | null> {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return getLocalOnboardingState();

    const { data, error } = await supabase
      .from("events")
      .select("payload, created_at")
      .eq("user_id", user.user.id)
      .eq("type", "ONBOARDING_STATE")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching onboarding state:", error);
      return getLocalOnboardingState();
    }

    if (data) {
      return data.payload as OnboardingState;
    }

    return getLocalOnboardingState();
  } catch (error) {
    console.error("Error in getUserOnboardingState:", error);
    return getLocalOnboardingState();
  }
}

export async function saveOnboardingState(
  state: OnboardingState
): Promise<void> {
  try {
    // Always save to localStorage first
    setLocalOnboardingState(state);

    // Save to database if user is authenticated
    const { data: user } = await supabase.auth.getUser();
    if (user.user) {
      const { error } = await supabase.from("events").insert({
        user_id: user.user.id,
        type: "ONBOARDING_STATE",
        payload: state,
      });

      if (error) {
        console.error("Error saving onboarding state:", error);
      }
    }
  } catch (error) {
    console.error("Error in saveOnboardingState:", error);
  }
}

export async function markOnboardingCompleted(): Promise<void> {
  const state: OnboardingState = {
    hasCompletedOnboarding: true,
    currentStep: -1,
    lastShownAt: new Date().toISOString(),
    skippedAt: null,
  };

  await saveOnboardingState(state);
}

export async function markOnboardingSkipped(): Promise<void> {
  const state: OnboardingState = {
    hasCompletedOnboarding: false,
    currentStep: -1,
    lastShownAt: new Date().toISOString(),
    skippedAt: new Date().toISOString(),
  };

  await saveOnboardingState(state);
}

export async function resetOnboarding(): Promise<void> {
  const state: OnboardingState = {
    hasCompletedOnboarding: false,
    currentStep: 0,
    lastShownAt: null,
    skippedAt: null,
  };

  await saveOnboardingState(state);
}

export async function shouldShowOnboarding(): Promise<boolean> {
  try {
    const state = await getUserOnboardingState();

    // Show if no state exists (new user)
    if (!state) return true;

    // Don't show if completed
    if (state.hasCompletedOnboarding) return false;

    // Don't show if skipped recently (within 7 days)
    if (state.skippedAt) {
      const skippedDate = new Date(state.skippedAt);
      const daysSinceSkipped =
        (Date.now() - skippedDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceSkipped > 7;
    }

    return true;
  } catch (error) {
    console.error("Error in shouldShowOnboarding:", error);
    return true; // Default to showing onboarding on errors
  }
}

export async function updateOnboardingStep(step: number): Promise<void> {
  try {
    const currentState = (await getUserOnboardingState()) || {
      hasCompletedOnboarding: false,
      currentStep: 0,
      lastShownAt: null,
      skippedAt: null,
    };

    const updatedState: OnboardingState = {
      ...currentState,
      currentStep: step,
      lastShownAt: new Date().toISOString(),
    };

    await saveOnboardingState(updatedState);
  } catch (error) {
    console.error("Error updating onboarding step:", error);
  }
}
